import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  Alert,
  CircularProgress,
  Chip,
  IconButton
} from '@mui/material';
import {
  Close,
  Download,
  Delete as DeleteIcon,
  Visibility,
  Person,
  CalendarToday
} from '@mui/icons-material';
import { EntityDocumentView } from './types';

interface ViewDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  document: EntityDocumentView | null;
  currentUserName: string;
  onDownload: (documentId: string) => Promise<Blob>;
  onDelete?: (documentId: string) => Promise<void>;
  /** When true, no delete option is ever offered, regardless of document ownership. */
  readOnly?: boolean;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const ViewDocumentDialog: React.FC<ViewDocumentDialogProps> = ({
  open,
  onClose,
  document: documentInfo,
  currentUserName,
  onDownload,
  onDelete,
  readOnly = false
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isViewing, setIsViewing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canDeleteDocument = Boolean(!readOnly && onDelete && currentUserName && documentInfo && documentInfo.uploadedBy === currentUserName);

  if (!documentInfo) return null;

  const handleDownload = async () => {
    setIsDownloading(true);
    setError(null);

    try {
      const blob = await onDownload(documentInfo.id);

      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = documentInfo.fileName;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: unknown) {
      const message = err instanceof Object && 'data' in err && (err as { data?: { message?: string } }).data?.message
        ? String((err as { data: { message: string } }).data.message)
        : 'Failed to download document. Please try again.';
      setError(message);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleView = async () => {
    setIsViewing(true);
    setError(null);

    try {
      const blob = await onDownload(documentInfo.id);
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      window.URL.revokeObjectURL(url);
    } catch (err: unknown) {
      const message = err instanceof Object && 'data' in err && (err as { data?: { message?: string } }).data?.message
        ? String((err as { data: { message: string } }).data.message)
        : 'Failed to view document. Please try again.';
      setError(message);
    } finally {
      setIsViewing(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    const confirmDelete = window.confirm(`Are you sure you want to delete "${documentInfo.fileName}"? This action cannot be undone.`);
    if (!confirmDelete) return;

    setIsDeleting(true);
    setError(null);

    try {
      await onDelete(documentInfo.id);
      onClose();
    } catch (err: unknown) {
      const message = err instanceof Object && 'data' in err && (err as { data?: { message?: string } }).data?.message
        ? String((err as { data: { message: string } }).data.message)
        : 'Failed to delete document. Please try again.';
      setError(message);
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'Unknown';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return 'Invalid date';
    }
  };

  const getFileTypeColor = (extension: string) => {
    switch (extension.toLowerCase()) {
      case '.pdf':
        return 'error';
      case '.doc':
      case '.docx':
        return 'primary';
      case '.xls':
      case '.xlsx':
        return 'success';
      case '.png':
      case '.jpg':
      case '.jpeg':
      case '.gif':
        return 'secondary';
      default:
        return 'default';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Document Details</Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h6" gutterBottom>
              {documentInfo.fileName}
            </Typography>
            <Chip
              label={documentInfo.extension.toUpperCase()}
              color={getFileTypeColor(documentInfo.extension)}
              size="small"
            />
          </Box>

          <Stack spacing={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <Person fontSize="small" color="action" />
              <Typography variant="body2">
                <strong>Uploaded by:</strong> {documentInfo.uploadedBy || 'Unknown'}
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={1}>
              <CalendarToday fontSize="small" color="action" />
              <Typography variant="body2">
                <strong>Upload date:</strong> {formatDate(documentInfo.uploadedDate)}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={isViewing ? <CircularProgress size={16} /> : <Visibility />}
              onClick={handleView}
              disabled={isViewing || isDownloading || isDeleting}
            >
              View
            </Button>

            <Button
              variant="outlined"
              startIcon={isDownloading ? <CircularProgress size={16} /> : <Download />}
              onClick={handleDownload}
              disabled={isViewing || isDownloading || isDeleting}
            >
              Download
            </Button>

            {/* Only show delete button if current user uploaded the document */}
            {canDeleteDocument && (
              <Button
                variant="outlined"
                color="error"
                startIcon={isDeleting ? <CircularProgress size={16} /> : <DeleteIcon />}
                onClick={handleDelete}
                disabled={isViewing || isDownloading || isDeleting}
              >
                Delete
              </Button>
            )}
          </Stack>

          {error && (
            <Alert severity="error">
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewDocumentDialog;
